// Inline renderer used while assistant tokens stream into a message bubble.
// Handles two inline patterns safely (no innerHTML, no HTML parser):
//   1. **bold text** → <strong>bold text</strong>
//   2. phone numbers (xx-xxx-xxxx) → <a href="tel:..."> for one-tap calling
// Anything else is plain text. The bubble is rebuilt on every stream tick.

// Phone numbers — allow 4-digit prefixes (0507 안심번호) in addition to 02/031/010.
const PHONE_RE = /(\d{2,4}-\d{3,4}-\d{4})/g;
const BOLD_RE = /\*\*([^*\n][^*\n]*?)\*\*/g;
// Markdown image: ![alt](https://...) — only http(s) URLs are accepted so a
// stray "![foo](javascript:...)" can't sneak through.
const IMG_RE = /!\[([^\]]*)\]\((https?:\/\/[^\s)]+)\)/g;
// Markdown link: [label](https://...) or [label](tel:0507-...) — limited URL
// schemes so other schemes (javascript:, data:) can't slip through.
const LINK_RE = /\[([^\]\n]+)\]\((https?:\/\/[^\s)]+|tel:[^\s)]+)\)/g;

type Segment =
  | { kind: 'text'; value: string }
  | { kind: 'bold'; value: string }
  | { kind: 'tel'; value: string }
  | { kind: 'img'; alt: string; url: string }
  | { kind: 'link'; label: string; url: string };

function splitImages(text: string): Segment[] {
  const out: Segment[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  IMG_RE.lastIndex = 0;
  while ((m = IMG_RE.exec(text)) !== null) {
    if (m.index > lastIndex) out.push({ kind: 'text', value: text.slice(lastIndex, m.index) });
    out.push({ kind: 'img', alt: m[1], url: m[2] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) out.push({ kind: 'text', value: text.slice(lastIndex) });
  return out.length ? out : [{ kind: 'text', value: text }];
}

function splitLinks(seg: Segment): Segment[] {
  if (seg.kind !== 'text') return [seg];
  const text = seg.value;
  const out: Segment[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  LINK_RE.lastIndex = 0;
  while ((m = LINK_RE.exec(text)) !== null) {
    if (m.index > lastIndex) out.push({ kind: 'text', value: text.slice(lastIndex, m.index) });
    out.push({ kind: 'link', label: m[1], url: m[2] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) out.push({ kind: 'text', value: text.slice(lastIndex) });
  return out.length ? out : [seg];
}

function splitBold(seg: Segment): Segment[] {
  if (seg.kind !== 'text') return [seg];
  const text = seg.value;
  const out: Segment[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  BOLD_RE.lastIndex = 0;
  while ((m = BOLD_RE.exec(text)) !== null) {
    if (m.index > lastIndex) out.push({ kind: 'text', value: text.slice(lastIndex, m.index) });
    out.push({ kind: 'bold', value: m[1] });
    lastIndex = m.index + m[0].length;
  }
  if (lastIndex < text.length) out.push({ kind: 'text', value: text.slice(lastIndex) });
  return out.length ? out : [seg];
}

function splitPhones(seg: Segment): Segment[] {
  if (seg.kind !== 'text') return [seg];
  const out: Segment[] = [];
  let lastIndex = 0;
  let m: RegExpExecArray | null;
  PHONE_RE.lastIndex = 0;
  while ((m = PHONE_RE.exec(seg.value)) !== null) {
    if (m.index > lastIndex) out.push({ kind: 'text', value: seg.value.slice(lastIndex, m.index) });
    out.push({ kind: 'tel', value: m[1] });
    lastIndex = m.index + m[1].length;
  }
  if (lastIndex < seg.value.length) out.push({ kind: 'text', value: seg.value.slice(lastIndex) });
  return out.length ? out : [seg];
}

export function renderTextWithTelLinks(target: HTMLElement, text: string): void {
  while (target.firstChild) target.removeChild(target.firstChild);
  // Four-pass split: image markdown → markdown link → bold → phone.
  const segments = splitImages(text).flatMap(splitLinks).flatMap(splitBold).flatMap(splitPhones);
  for (const seg of segments) {
    if (seg.kind === 'text') {
      target.appendChild(document.createTextNode(seg.value));
    } else if (seg.kind === 'bold') {
      const strong = document.createElement('strong');
      strong.textContent = seg.value;
      target.appendChild(strong);
    } else if (seg.kind === 'img') {
      const img = document.createElement('img');
      img.src = seg.url;
      img.alt = seg.alt;
      img.className = 'cbsa-msg-img';
      img.loading = 'lazy';
      img.decoding = 'async';
      img.onerror = () => img.remove();
      target.appendChild(img);
    } else if (seg.kind === 'link') {
      const a = document.createElement('a');
      a.href = seg.url;
      a.target = '_blank';
      a.rel = 'noopener noreferrer';
      a.className = 'cbsa-msg-link';
      a.textContent = seg.label;
      target.appendChild(a);
    } else {
      const a = document.createElement('a');
      a.setAttribute('href', `tel:${seg.value}`);
      a.style.color = 'inherit';
      a.style.textDecoration = 'underline';
      a.textContent = seg.value;
      target.appendChild(a);
    }
  }
}
